import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Plus, Loader2, Save, Eye, RefreshCw, Sparkles } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import CourseSettings from '@/components/course-builder/CourseSettings';
import ModuleCard from '@/components/course-builder/ModuleCard';
import LessonEditor from '@/components/course-builder/LessonEditor';
import InterviewLibrarySection from '@/components/course-builder/InterviewLibrarySection';
import RosterCard from '@/components/creation-flow/RosterCard';
import CourseConciergePanel from '@/components/course-builder/CourseConciergePanel';
import VideoEditDrawer from '@/components/video-editor/VideoEditDrawer';
import { RecordingModal } from '@/components/recording/RecordingModal';
import LibraryPicker, { type StagedLibraryFile } from '@/components/library/LibraryPicker';
import { createProduct, updateProduct, fetchProductForEdit, getProductFileUploadUrl, uploadToSignedUrl, requestMuxIngest, syncMuxStatus, type CreatorProduct, type CourseModule, type CourseLesson, type ProductVideo, type ConciergeOutline, type MuxPreview } from '@/services/creatorApi';
import { createInterview } from '@/services/interviewApi';
import { Users } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

function genId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

// Pull Mux fields off the flat product.videos[] and attach them to the matching
// lesson (by s3Key). Lessons in the editor mirror videos[] one-for-one because
// CourseBuilderPage saves a flatVideos array built from modules.flatMap.
function hydrateLessonMux(modules: CourseModule[], videos: ProductVideo[]): CourseModule[] {
  if (!videos || !videos.length) return modules;
  const byKey = new Map(videos.filter(v => v.s3Key).map(v => [v.s3Key, v]));
  return modules.map(m => ({
    ...m,
    lessons: m.lessons.map(l => {
      const v = byKey.get(l.s3Key);
      if (!v) return l;
      return {
        ...l,
        muxAssetId: v.muxAssetId,
        muxPlaybackId: v.muxPlaybackId,
        muxIngestStatus: v.muxIngestStatus,
        muxIngestError: v.muxIngestError,
      };
    }),
  }));
}

const CourseBuilderPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isEditing = !!productId;
  // Prefill from the AI chooser (new course only). Ignored when editing.
  // `idea` is the creator's own typed description — it seeds the concierge so
  // "Set it up" actually carries their context into the AI builder.
  const prefill = (location.state || {}) as { title?: string; description?: string; priceCents?: number; idea?: string };

  // Course settings
  const [title, setTitle] = useState(prefill.title || '');
  const [description, setDescription] = useState(prefill.description || '');
  const [price, setPrice] = useState(prefill.priceCents != null && (prefill.priceCents === 0 || prefill.priceCents >= 100) ? prefill.priceCents : 999); // $9.99 default; honors an AI "free" suggestion
  const [pricingMode, setPricingMode] = useState<'one_time' | 'subscription'>('one_time');
  const [coverImage, setCoverImage] = useState('');
  const [published, setPublished] = useState(false);
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [plusOfferMonths, setPlusOfferMonths] = useState(0);
  const [includedProductIds, setIncludedProductIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Modules
  const [modules, setModules] = useState<CourseModule[]>([]);

  // Lesson editor
  const [lessonEditorOpen, setLessonEditorOpen] = useState(false);
  const [editingModuleIndex, setEditingModuleIndex] = useState<number>(-1);
  const [editingLessonIndex, setEditingLessonIndex] = useState<number>(-1);
  const [editingLesson, setEditingLesson] = useState<Partial<CourseLesson> | null>(null);

  // Loading
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Dirty state — true once the user has changed anything since the last save.
  // The save button gets visually emphasized and a beforeunload guard fires.
  // Reset on load and on successful save.
  const [dirty, setDirty] = useState(false);
  const initialLoadRef = useRef(true);
  // Status announcement for AT users.
  const [statusMsg, setStatusMsg] = useState('');
  const [syncingMux, setSyncingMux] = useState(false);

  // Intro video — a short pitch clip shown on the public course page. Same
  // "preview clip" concept video products have: a public Mux asset provisioned
  // on save via requestMuxIngest({ kind: 'preview' }). NOT a lesson.
  const [previewClipS3Key, setPreviewClipS3Key] = useState<string | null>(null);
  const [previewClipName, setPreviewClipName] = useState<string | null>(null);
  const [uploadingPreviewClip, setUploadingPreviewClip] = useState(false);
  const [previewPercent, setPreviewPercent] = useState<number | null>(null);
  const [existingMuxPreview, setExistingMuxPreview] = useState<MuxPreview | null>(null);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [editingPreview, setEditingPreview] = useState(false);

  // Guided "build with me" concierge panel.
  // Auto-open when arriving from the AI chooser with a typed idea, so the
  // concierge picks up right where "Set it up" left off.
  const [conciergeOpen, setConciergeOpen] = useState(!isEditing && !!prefill.idea);

  // Merge-aware outline apply. The concierge returns a COMPLETE revised
  // curriculum reusing existing module/lesson ids for anything kept — we
  // preserve those lessons' payloads (recorded video, Mux fields, drip, etc.)
  // and overwrite only title/description/order. Modules/lessons without a
  // matching id are new and get the empty defaults. Used for both the
  // idea-phase draft and ongoing curriculum chat, so a redraft never wipes
  // already-recorded footage.
  const applyCurriculumOutline = (o: ConciergeOutline) => {
    if (o.title) setTitle(o.title);
    if (o.description) setDescription(o.description);
    setModules(prev => {
      const lessonById = new Map<string, CourseLesson>();
      const moduleById = new Map<string, CourseModule>();
      prev.forEach(m => {
        moduleById.set(m.id, m);
        m.lessons.forEach(l => lessonById.set(l.id, l));
      });
      return o.modules.map((m, mi) => ({
        id: (m.id && moduleById.has(m.id)) ? m.id : genId(),
        title: m.title,
        order: mi,
        lessons: m.lessons.map((l, li) => {
          const existing = l.id ? lessonById.get(l.id) : undefined;
          if (existing) {
            // Preserve the recorded payload; overwrite only text + order.
            return { ...existing, title: l.title, description: l.description, order: li };
          }
          return { id: genId(), title: l.title, description: l.description, s3Key: '', fileType: 'video' as const, freePreview: false, dripDay: 0, order: li };
        }),
      }));
    });
  };

  const attachConciergeVideo = (moduleId: string, lessonId: string, video: Partial<CourseLesson>) => {
    setModules(prev => prev.map(m => m.id === moduleId
      ? { ...m, lessons: m.lessons.map(l => l.id === lessonId ? { ...l, ...video } : l) }
      : m));
  };

  // Inline video edit drawer — one instance for the whole page; state points at
  // the lesson being edited. On apply, the finalized patch lands on the lesson.
  const [videoEditTarget, setVideoEditTarget] = useState<{ moduleId: string; lessonId: string; s3Key: string; title?: string; muxPlaybackId?: string } | null>(null);

  const openLessonVideoEditor = (moduleIndex: number, lessonIndex: number) => {
    const mod = modules[moduleIndex];
    const lesson = mod?.lessons[lessonIndex];
    if (!mod || !lesson?.s3Key) return;
    setVideoEditTarget({ moduleId: mod.id, lessonId: lesson.id, s3Key: lesson.s3Key, title: lesson.title, muxPlaybackId: lesson.muxPlaybackId });
  };

  // Load existing product
  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    fetchProductForEdit(productId)
      .then(product => {
        if (!product) { toast({ title: 'Course not found', variant: 'destructive' }); navigate('/my-store'); return; }
        setTitle(product.title);
        setDescription(product.description);
        setPrice(product.price);
        setPricingMode(product.pricingMode || 'one_time');
        setCoverImage(product.coverImage || '');
        setPublished(product.published);
        setCategory((product as any).category || '');
        setTags((product as any).tags || []);
        setPlusOfferMonths((product as any).plusOfferMonths || 0);
        setIncludedProductIds(product.includedProductIds || []);
        setExistingMuxPreview(product.muxPreview || null);
        if (product.modules && product.modules.length > 0) {
          setModules(hydrateLessonMux(product.modules, product.videos));
        } else if (product.videos.length > 0) {
          // Migrate flat videos to a single module
          const migrated: CourseModule[] = [{
            id: genId(),
            title: 'Module 1',
            order: 0,
            lessons: product.videos.map((v, i) => ({
              id: genId(),
              title: v.title,
              description: '',
              s3Key: v.s3Key,
              fileType: v.fileType || 'video',
              fileSize: v.fileSize,
              duration: v.duration,
              thumbnailUrl: v.thumbnailUrl,
              freePreview: v.freePreview || false,
              dripDay: v.dripDay || 0,
              availableDate: v.availableDate,
              order: i,
            })),
          }];
          setModules(hydrateLessonMux(migrated, product.videos));
        }
      })
      .catch(err => { toast({ title: 'Error', description: err.message, variant: 'destructive' }); })
      .finally(() => {
        setLoading(false);
        // The setState calls above schedule the next render's dirty-watch effect
        // to fire; mark this as the load-induced render so it doesn't flip dirty.
        initialLoadRef.current = true;
        setDirty(false);
      });
  }, [productId]);

  // Mark dirty on any user-driven state change. Skips the first render and the
  // post-load render (which would otherwise mark every freshly-loaded course
  // as dirty).
  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }
    setDirty(true);
  }, [title, description, price, pricingMode, coverImage, published, modules, category, tags, plusOfferMonths, previewClipS3Key]);

  // beforeunload guard so the browser asks before reload/close if there are
  // unsaved changes. SPA navigations are warned via toast in handleSave path.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const handleBackClick = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    navigate('/my-store');
  };

  // Manual Mux status refresh — polls /api/mux/sync, then refetches the product
  // and re-hydrates lesson Mux fields. Useful when the webhook is missing or
  // delayed and a lesson is stuck on "processing."
  const handleRefreshMux = async () => {
    if (!productId || syncingMux) return;
    setSyncingMux(true);
    try {
      const result = await syncMuxStatus(productId);
      if (result === null) {
        toast({ title: 'Video processing unavailable', description: 'Video processing is not set up on this server.', variant: 'destructive' });
        return;
      }
      // Refetch the product so the updated muxIngestStatus shows up immediately.
      const fresh = await fetchProductForEdit(productId);
      if (fresh && fresh.modules && fresh.modules.length > 0) {
        initialLoadRef.current = true;
        setModules(hydrateLessonMux(fresh.modules, fresh.videos));
      }
      const readyCount = result.results.filter(r => r.status === 'ready').length;
      const erroredCount = result.results.filter(r => r.status === 'errored').length;
      const preparingCount = result.results.filter(r => r.status === 'preparing').length;
      const msg = `${readyCount} ready · ${preparingCount} processing · ${erroredCount} failed`;
      toast({ title: 'Video status updated', description: msg });
      setStatusMsg(`Video status updated: ${msg}`);
    } catch (err: any) {
      toast({ title: 'Refresh failed', description: err.message, variant: 'destructive' });
    } finally {
      setSyncingMux(false);
    }
  };

  // Auto-sync on load if any lesson is stuck in 'preparing' (so the editor
  // self-heals when the user lands without doing anything). Fires once after
  // the load effect resolves.
  useEffect(() => {
    if (loading || !productId) return;
    const hasPreparing = modules.some(m => m.lessons.some(l =>
      l.muxIngestStatus === 'preparing' && l.fileType === 'video'
    ));
    if (!hasPreparing) return;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    handleRefreshMux();
    // Run only on the first ready-render with a preparing lesson; dependency on
    // modules.length avoids re-firing for every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, productId]);

  // --- Intro video (public preview clip) ---
  // Stage a clip file into the product bucket (shared by upload + record). The
  // Mux asset is created at save via the preview-kind ingest fan-out.
  const stagePreviewFile = async (file: File, label?: string) => {
    setUploadingPreviewClip(true); setPreviewPercent(0);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(`preview-${file.name}`, file.type);
      await uploadToSignedUrl(uploadUrl, file, setPreviewPercent);
      setPreviewClipS3Key(key); setPreviewClipName(label || file.name); setExistingMuxPreview(null);
      toast({ title: 'Intro video added — save to finalize' });
    } catch (err: any) {
      toast({ title: 'Intro video upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingPreviewClip(false); setPreviewPercent(null); }
  };

  const handlePreviewClipUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { toast({ title: 'Intro video must be a video file', variant: 'destructive' }); return; }
    if (file.size > 1024 * 1024 * 1024) { toast({ title: 'Intro video too large', description: 'Max 1GB.', variant: 'destructive' }); return; }
    await stagePreviewFile(file);
    e.target.value = '';
  };

  const handlePreviewRecorded = async (file: File) => {
    setShowRecordModal(false);
    await stagePreviewFile(file, 'Recorded intro');
  };

  // Library pick — LibraryPicker stages into creator-products/ (lesson target)
  // and hands back a Mux-ingestable key + proxy URL, exactly like an upload.
  const handlePreviewLibraryPick = (f: StagedLibraryFile) => {
    setPreviewClipS3Key(f.key); setPreviewClipName(f.name); setExistingMuxPreview(null);
    toast({ title: 'Intro video selected — save to finalize' });
  };

  const removePreviewClip = () => {
    setPreviewClipS3Key(null); setPreviewClipName(null); setExistingMuxPreview(null);
  };

  // Drawer apply: swap in the edited clip and clear the live preview so the
  // save-time ingest re-runs against the new key.
  const applyPreviewEdit = (patch: { s3Key: string }) => {
    setPreviewClipS3Key(patch.s3Key);
    setPreviewClipName(prev => prev || 'Edited intro');
    setExistingMuxPreview(null);
    setEditingPreview(false);
  };

  const previewEditKey = previewClipS3Key || existingMuxPreview?.sourceS3Key || null;

  // --- Module operations ---
  const addModule = () => {
    setModules(prev => [...prev, {
      id: genId(),
      title: `Module ${prev.length + 1}`,
      order: prev.length,
      lessons: [],
    }]);
  };

  const updateModule = (index: number, updated: CourseModule) => {
    setModules(prev => prev.map((m, i) => i === index ? updated : m));
  };

  const deleteModule = (index: number) => {
    if (modules[index].lessons.length > 0 && !confirm(`Delete "${modules[index].title}" and all its lessons?`)) return;
    setModules(prev => prev.filter((_, i) => i !== index));
  };

  const reorderModules = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(modules);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setModules(items.map((m, i) => ({ ...m, order: i })));
  };

  // --- Lesson operations ---
  const openAddLesson = (moduleIndex: number) => {
    setEditingModuleIndex(moduleIndex);
    setEditingLessonIndex(-1);
    setEditingLesson({});
    setLessonEditorOpen(true);
  };

  const openEditLesson = (moduleIndex: number, lessonIndex: number) => {
    setEditingModuleIndex(moduleIndex);
    setEditingLessonIndex(lessonIndex);
    setEditingLesson(modules[moduleIndex].lessons[lessonIndex]);
    setLessonEditorOpen(true);
  };

  const handleSaveLesson = (lessonData: Partial<CourseLesson>) => {
    setModules(prev => {
      const updated = [...prev];
      const mod = { ...updated[editingModuleIndex], lessons: [...updated[editingModuleIndex].lessons] };
      if (editingLessonIndex >= 0) {
        // Update existing
        mod.lessons[editingLessonIndex] = { ...mod.lessons[editingLessonIndex], ...lessonData } as CourseLesson;
      } else {
        // Add new
        mod.lessons.push({
          id: genId(),
          title: '',
          s3Key: '',
          fileType: 'video',
          freePreview: false,
          dripDay: 0,
          order: mod.lessons.length,
          ...lessonData,
        } as CourseLesson);
      }
      mod.lessons = mod.lessons.map((l, i) => ({ ...l, order: i }));
      updated[editingModuleIndex] = mod;
      return updated;
    });
  };

  const deleteLesson = (moduleIndex: number, lessonIndex: number) => {
    setModules(prev => {
      const updated = [...prev];
      const mod = { ...updated[moduleIndex], lessons: [...updated[moduleIndex].lessons] };
      mod.lessons.splice(lessonIndex, 1);
      mod.lessons = mod.lessons.map((l, i) => ({ ...l, order: i }));
      updated[moduleIndex] = mod;
      return updated;
    });
  };

  const reorderLessons = (moduleIndex: number, startIndex: number, endIndex: number) => {
    setModules(prev => {
      const updated = [...prev];
      const mod = { ...updated[moduleIndex], lessons: [...updated[moduleIndex].lessons] };
      const [moved] = mod.lessons.splice(startIndex, 1);
      mod.lessons.splice(endIndex, 0, moved);
      mod.lessons = mod.lessons.map((l, i) => ({ ...l, order: i }));
      updated[moduleIndex] = mod;
      return updated;
    });
  };

  // --- Save ---
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!title.trim()) errs.title = 'Title is required';
    if (price > 0 && price < 100) errs.price = 'Price must be $0 (free) or at least $1.00';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // Build flat videos array from modules for backward compat
      const flatVideos = modules.flatMap(m =>
        m.lessons.map(l => ({
          title: l.title,
          s3Key: l.s3Key,
          fileType: l.fileType,
          fileSize: l.fileSize,
          duration: l.duration,
          thumbnailUrl: l.thumbnailUrl,
          freePreview: l.freePreview,
          dripDay: l.dripDay,
          availableDate: l.availableDate,
          // Round-trip streaming fields from upload-time ingest so the asset
          // created before save attaches to the persisted video row.
          muxAssetId: l.muxAssetId,
          muxPlaybackId: l.muxPlaybackId,
          muxIngestStatus: l.muxIngestStatus,
        }))
      );

      const payload = {
        title: title.trim(),
        description: description.trim(),
        price,
        productType: 'series' as const,
        pricingMode,
        subscriptionInterval: pricingMode === 'subscription' ? 'month' as const : undefined,
        coverImage: coverImage || undefined,
        published,
        category: category || null,
        tags,
        plusOfferMonths: plusOfferMonths || null,
        includedProductIds: pricingMode === 'subscription' ? includedProductIds : [],
        videos: flatVideos,
        modules: modules.map((m, i) => ({ ...m, order: i })),
      };

      const saved = isEditing
        ? await updateProduct(productId!, payload)
        : await createProduct(payload);

      // Ingest fan-out fallback — lessons normally start processing at upload
      // time (LessonEditor source-only ingest), so this only catches videos
      // whose upload-time ingest failed or predates that flow. Webhook updates
      // muxIngestStatus when ingest completes. Failures (403 disabled / 503
      // unconfigured / real errors) are logged but don't block the save.
      if (saved && saved._id) {
        const ingestPromises: Promise<unknown>[] = [];
        (saved.videos || []).forEach((v, idx) => {
          if (!v.s3Key) return;
          if (v.fileType && v.fileType !== 'video') return; // only ingest video files
          if (v.muxAssetId) return;
          ingestPromises.push(
            requestMuxIngest({ productId: saved._id, videoIndex: idx, s3Key: v.s3Key, kind: 'paid' })
              .catch(err => console.warn('[mux] lesson ingest failed for video', idx, err))
          );
        });
        if (ingestPromises.length) {
          await Promise.race([
            Promise.allSettled(ingestPromises),
            new Promise(resolve => setTimeout(resolve, 4000)),
          ]);
        }
      }

      // Intro-video Mux fan-out — provision a public preview asset when a new
      // clip was staged and it isn't the one already on the product. muxPreview
      // is written server-side by the ingest endpoint (not the product save).
      if (saved && saved._id && previewClipS3Key &&
          (!saved.muxPreview || saved.muxPreview.sourceS3Key !== previewClipS3Key)) {
        await Promise.race([
          requestMuxIngest({ productId: saved._id, s3Key: previewClipS3Key, kind: 'preview' })
            .catch(err => console.warn('[mux] preview ingest failed', err)),
          new Promise(resolve => setTimeout(resolve, 4000)),
        ]);
      }

      if (isEditing) {
        toast({ title: 'Course updated!' });
        setStatusMsg('Course saved. Video lessons are processing and will be ready to stream shortly.');
        // Refresh in place so newly-set muxAssetId / muxIngestStatus surfaces
        // on the lesson rows without a manual reload.
        try {
          const fresh = await fetchProductForEdit(productId);
          if (fresh && fresh.modules && fresh.modules.length > 0) {
            initialLoadRef.current = true;
            setModules(hydrateLessonMux(fresh.modules, fresh.videos));
          }
        } catch { /* non-fatal */ }
        setDirty(false);
      } else {
        toast({ title: 'Course created!' });
        setStatusMsg('Course created. Video lessons are processing and will be ready to stream shortly.');
        setDirty(false);
        navigate(`/my-store/course/${saved._id}`, { replace: true });
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const totalLessons = modules.reduce((sum, m) => sum + m.lessons.length, 0);

  if (loading) {
    return (
      <main
        className="min-h-screen flex items-center justify-center"
        data-paper="beige"
        style={{ background: 'var(--hj-beige)' }}
      >
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-beige-ink-3)' }} />
      </main>
    );
  }

  return (
    <main
      className="min-h-screen"
      data-paper="beige"
      style={{ background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)' }}
    >
      {/* Top bar */}
      <div
        className="sticky top-0 z-10"
        style={{
          background: 'var(--hj-beige)',
          borderBottom: '1px solid var(--hj-beige-line)',
        }}
      >
        <div
          className="mx-auto flex items-center justify-between gap-3"
          style={{ maxWidth: 1280, padding: '14px 16px' }}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              type="button"
              onClick={handleBackClick}
              aria-label="Back to My Store"
              className="flex-shrink-0 inline-flex items-center justify-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-beige-line)',
                color: 'var(--hj-beige-ink-2)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)' }}>
                {isEditing ? 'Edit course' : 'New course'}
              </div>
              <h1
                className="font-hj-display m-0 truncate"
                style={{
                  fontSize: 18,
                  color: 'var(--hj-beige-ink)',
                  letterSpacing: '-0.02em',
                  fontWeight: 700,
                  marginTop: 2,
                }}
              >
                {title.trim() || 'Untitled course'}
              </h1>
              <p
                className="m-0 tabular"
                style={{ marginTop: 2, fontSize: 11.5, color: 'var(--hj-beige-ink-3)', fontFamily: 'var(--hj-font-mono)' }}
              >
                {modules.length} module{modules.length !== 1 ? 's' : ''} · {totalLessons} lesson
                {totalLessons !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isEditing && (
              <>
                <button
                  type="button"
                  onClick={handleRefreshMux}
                  disabled={syncingMux}
                  aria-label="Refresh video processing status"
                  className="inline-flex items-center gap-1.5 disabled:cursor-not-allowed"
                  style={{
                    padding: '8px 14px',
                    borderRadius: 999,
                    background: 'transparent',
                    border: '1px solid var(--hj-beige-line)',
                    color: 'var(--hj-beige-ink-2)',
                    fontSize: 12.5,
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    cursor: syncingMux ? 'not-allowed' : 'pointer',
                    minHeight: 38,
                  }}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${syncingMux ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh status</span>
                </button>
                <a
                  href={`/purchases/${productId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5"
                  style={{
                    padding: '8px 14px',
                    borderRadius: 999,
                    background: 'transparent',
                    border: '1px solid var(--hj-beige-line)',
                    color: 'var(--hj-beige-ink-2)',
                    fontSize: 12.5,
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    textDecoration: 'none',
                    minHeight: 38,
                  }}
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Preview</span>
                </a>
              </>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              aria-label={
                saving
                  ? 'Saving course'
                  : dirty
                    ? (isEditing ? 'Save changes — you have unsaved edits' : 'Create course')
                    : (isEditing ? 'Save changes (nothing to save)' : 'Create course')
              }
              className={`inline-flex items-center gap-1.5 disabled:cursor-not-allowed ${dirty && !saving ? 'animate-pulse' : ''}`}
              style={{
                padding: '9px 18px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: dirty ? '2px solid oklch(0.45 0.15 80)' : '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 13,
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
                minHeight: 40,
                boxShadow: dirty && !saving ? '0 0 0 3px rgb(255 214 0 / 0.35)' : 'none',
              }}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{isEditing ? 'Save changes' : 'Create course'}</span>
              <span className="sm:hidden">Save</span>
              {dirty && !saving && (
                <span
                  className="inline-block"
                  aria-hidden="true"
                  style={{ width: 6, height: 6, borderRadius: '50%', background: 'oklch(0.30 0.12 30)' }}
                />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Unsaved changes banner — instructs the user about the save→Mux-ingest dependency */}
      {dirty && (
        <div
          role="status"
          aria-live="polite"
          style={{
            background: 'rgb(255 214 0 / 0.18)',
            borderBottom: '1px solid rgb(255 214 0 / 0.5)',
          }}
        >
          <div
            className="mx-auto flex items-center justify-between gap-3"
            style={{ maxWidth: 1280, padding: '10px 16px' }}
          >
            <p
              className="m-0"
              style={{ fontSize: 13, color: 'var(--hj-beige-ink)', fontWeight: 500 }}
            >
              <strong>Unsaved changes.</strong> Click <em>Save changes</em> to publish your edits.
            </p>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 flex-shrink-0 disabled:cursor-not-allowed"
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                background: 'oklch(0.18 0.005 70)',
                border: '1px solid oklch(0.18 0.005 70)',
                color: '#fff',
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              Save now
            </button>
          </div>
        </div>
      )}

      {/* Live region for status announcements (Mux save, etc.). Off-screen but
          read by screen readers when statusMsg changes. */}
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {statusMsg}
      </div>

      {/* Main layout */}
      <div className="mx-auto" style={{ maxWidth: 1280, padding: '24px 16px 96px' }}>
        <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
          {/* Left: Settings */}
          <div className="w-full lg:w-80 lg:flex-shrink-0 lg:sticky" style={{ top: 88 }}>
            <CourseSettings
              title={title} setTitle={setTitle}
              description={description} setDescription={setDescription}
              price={price} setPrice={setPrice}
              pricingMode={pricingMode} setPricingMode={setPricingMode}
              coverImage={coverImage} setCoverImage={setCoverImage}
              published={published} setPublished={setPublished}
              category={category} setCategory={setCategory}
              tags={tags} setTags={setTags}
              plusOfferMonths={plusOfferMonths} setPlusOfferMonths={setPlusOfferMonths}
              includedProductIds={includedProductIds} setIncludedProductIds={setIncludedProductIds}
              errors={errors}
              productId={productId}
              introVideo={{
                previewClipS3Key,
                previewClipName,
                existingMuxPreview,
                uploading: uploadingPreviewClip,
                percent: previewPercent,
                editKey: previewEditKey,
                onUpload: handlePreviewClipUpload,
                onRecord: () => setShowRecordModal(true),
                onLibrary: () => setShowLibraryPicker(true),
                onRemove: removePreviewClip,
                onEdit: () => setEditingPreview(true),
              }}
            />
            {/* Who's enrolled — edit mode only. */}
            {isEditing && (
              <div style={{ marginTop: 20 }}>
                <RosterCard productId={productId} kicker="Students" noun="enrolled" fileLabel="students" title={title} />
              </div>
            )}
          </div>

          {/* Right: Modules */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <h2
                className="font-hj-display m-0"
                style={{
                  fontSize: 20,
                  color: 'var(--hj-beige-ink)',
                  letterSpacing: '-0.02em',
                  fontWeight: 700,
                }}
              >
                Curriculum
              </h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setConciergeOpen(true)}
                  className="inline-flex items-center gap-1.5"
                  style={{ padding: '7px 14px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', minHeight: 36 }}
                >
                  <Sparkles className="w-3.5 h-3.5" /> Build with me
                </button>
                <button
                  type="button"
                  onClick={async () => { try { const { interview } = await createInterview({ title: `${title.trim() || 'Course'} interview`, productId: isEditing ? productId : null }); navigate(`/my-store/interviews/${interview._id}`); } catch (e: any) { toast({ title: 'Failed', description: e.message, variant: 'destructive' }); } }}
                  className="inline-flex items-center gap-1.5"
                  style={{ padding: '7px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', minHeight: 36 }}
                >
                  <Users className="w-3.5 h-3.5" /> Interview a guest
                </button>
                <button
                  type="button"
                  onClick={addModule}
                  className="inline-flex items-center gap-1.5"
                  style={{
                    padding: '7px 14px',
                    borderRadius: 999,
                    background: 'transparent',
                    border: '1px solid var(--hj-beige-line)',
                    color: 'var(--hj-beige-ink-2)',
                    fontSize: 12.5,
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    minHeight: 36,
                  }}
                >
                  <Plus className="w-3.5 h-3.5" /> Add module
                </button>
              </div>
            </div>

            {/* Guided "build with me" concierge — runs inline in the curriculum
                column (the beige window), not as a full-screen takeover. */}
            {conciergeOpen && (
              <CourseConciergePanel
                open={conciergeOpen}
                onClose={() => setConciergeOpen(false)}
                courseTitle={title}
                courseDescription={description}
                modules={modules}
                applyOutline={applyCurriculumOutline}
                updateLesson={attachConciergeVideo}
                initialIdea={prefill.idea}
              />
            )}

            {!conciergeOpen && <InterviewLibrarySection productId={productId} />}

            {!conciergeOpen && (modules.length === 0 ? (
              <div
                className="text-center"
                style={{
                  padding: '56px 24px',
                  borderRadius: 'var(--hj-r-md)',
                  border: '1.5px dashed var(--hj-beige-line)',
                  background: '#fff',
                }}
              >
                <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-beige-ink-3)', marginBottom: 14 }}>
                  Build it with me — describe your course and I'll draft the outline, then we record it together. Or add modules yourself.
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setConciergeOpen(true)}
                    className="inline-flex items-center gap-1.5"
                    style={{ padding: '9px 18px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', minHeight: 40 }}
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Build with me
                  </button>
                  <button
                    type="button"
                    onClick={addModule}
                    className="inline-flex items-center gap-1.5"
                    style={{ padding: '9px 18px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', minHeight: 40 }}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add a module
                  </button>
                </div>
              </div>
            ) : (
              <DragDropContext onDragEnd={reorderModules}>
                <Droppable droppableId="modules">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col gap-3">
                      {modules.map((module, idx) => (
                        <Draggable key={module.id} draggableId={module.id} index={idx}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.draggableProps}>
                              <ModuleCard
                                module={module}
                                moduleIndex={idx}
                                onUpdateModule={(m) => updateModule(idx, m)}
                                onDeleteModule={() => deleteModule(idx)}
                                onAddLesson={() => openAddLesson(idx)}
                                onEditLesson={(li) => openEditLesson(idx, li)}
                                onDeleteLesson={(li) => deleteLesson(idx, li)}
                                onEditLessonVideo={(li) => openLessonVideoEditor(idx, li)}
                                onReorderLessons={(s, e) => reorderLessons(idx, s, e)}
                                dragHandleProps={provided.dragHandleProps}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ))}
          </div>
        </div>
      </div>

      {/* Lesson Editor Dialog */}
      <LessonEditor
        open={lessonEditorOpen}
        onClose={() => setLessonEditorOpen(false)}
        lesson={editingLesson}
        onSave={handleSaveLesson}
        isSubscription={pricingMode === 'subscription'}
        productId={productId}
      />

      {/* Inline video edit drawer (single page-level instance) */}
      {videoEditTarget && (
        <VideoEditDrawer
          open
          video={{ s3Key: videoEditTarget.s3Key, title: videoEditTarget.title, muxPlaybackId: videoEditTarget.muxPlaybackId }}
          onApplied={(patch) => attachConciergeVideo(videoEditTarget.moduleId, videoEditTarget.lessonId, patch)}
          onClose={() => setVideoEditTarget(null)}
        />
      )}

      {/* Intro-video sources + editor (single page-level instances) */}
      <RecordingModal open={showRecordModal} onClose={() => setShowRecordModal(false)} onSave={handlePreviewRecorded} />
      <LibraryPicker open={showLibraryPicker} stageTarget="lesson" onClose={() => setShowLibraryPicker(false)} onPick={handlePreviewLibraryPick} />
      {editingPreview && !!previewEditKey && (
        <VideoEditDrawer
          open
          video={{ s3Key: previewEditKey, title: previewClipName || 'Intro video', muxPlaybackId: existingMuxPreview?.muxPlaybackId }}
          onApplied={applyPreviewEdit}
          onClose={() => setEditingPreview(false)}
        />
      )}
    </main>
  );
};

export default CourseBuilderPage;
